import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useForm, ErrorMessage } from "react-hook-form";
import { Helmet } from 'react-helmet';
import clsx from 'clsx';
import withSizes from 'react-sizes';

import { Container, Row, Col } from "deltoid/core/Blocks/BootstrapContainers";
import { Card } from "deltoid/core/OneDashCards";
import { H, Caption, Small } from "deltoid/core/Blocks/Typography";
import Form from "deltoid/core/Form";

import SignInCMS from "../../features/prismic/SignInCMS";

import { createUser, fetchUser } from "../../../shared/actions/auth";
import { UserResponse } from "../../../shared/utils/response";
import { INTEGRATION_TYPE } from "../../../shared/utils/common";
import { fetchQueryParamsFromSession } from "../../../shared/services/utils";
import { 
  emailRegex,
  nameRegex,
  strongPasswordRegex,
  mobileNumberRegex,
  hasMinChars,
  hasUpperChar,
  hasLowerChar,
  hasNumber,
  hasSpecialChar,
  websiteUrlRegex,
} from '../../../shared/services/validations';
import AuthManager from '../../../shared/services/AuthManager';
import config from '../../../shared/services/config';

const RegisterUser = (props) => {
  const { user } = props;
  const { register, handleSubmit, errors, getValues, setError, clearError, watch, setValue, control } = useForm({
    defaultValues: {
      integrationType: INTEGRATION_TYPE.THIRD_PARTY,
    }
  });
  const [integrationType, setIntegrationType] = useState(INTEGRATION_TYPE.THIRD_PARTY);
  const [isDisabled, setIsDisabled] = useState(false);
  const isPartner = config.get('product') === 'PARTNER';
  const [open, setOpen] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasMin8Chars: false,
    hasUppercaseChar: false,
    hasLowercaseChar: false,
    hasDigit: false,
    hasSpecialChar: false,
  })

  useEffect(() => {
    const sessionParams = fetchQueryParamsFromSession();
    if(sessionParams && sessionParams.email) {
      const email = sessionParams.email;
      if(!emailRegex(email)) {
        setValue('email', email);
        fetchUserData()
      }
    }
    const {state: historyState } = props.location;
    if(historyState && historyState.userEmail){
      if(!emailRegex(historyState.userEmail)) {
        setValue('email', historyState.userEmail);
        fetchUserData()
      }
      setValue('password', historyState.password);
      setValue('mobile', historyState.user.mobile);
      isPartner && setValue('name', historyState.name);
    }
  },[])

  useEffect(() => {
    if(user && user.length > 0 ) {
      setError('email', 'validation', 'The user id already exist. Please try to Log in instead.')
      setIsDisabled(true);
      if (!!isStaffInviteLink) {
        props.history.push({
          pathname: "/account/signin",
        })
      }
    }
    else if(user &&  user.length == 0) {
      clearError('email')
      setIsDisabled(false);
    }
  }, [user])

  useEffect(() => {
    setIntegrationType(getValues('integrationType'))
  }, [watch(['integrationType'])])

  const className = clsx(
    'tooltip-container',
    {
      'mobile': false,
      'hide': !open,
    },
  );
  
  const getTickIcon = (success) => {
    return (
      <div className={`checkmark ${success ? 'success' : ''}`}>
        <div className="checkmark_circle" />
        <div className="checkmark_stem" />
        <div className="checkmark_kick" />
      </div>
    );
  }

  const getValidationContent = () => {
    const {
      hasMin8Chars,
      hasUppercaseChar,
      hasLowercaseChar,
      hasDigit,
      hasSpecialChar,
    } = passwordStrength;
    return (
      <div className="password-checklist-wrapper">
        <ul className="password-list p-0">
          <li>{getTickIcon(hasMin8Chars)} atleast 8 characters</li>
          <li>{getTickIcon(hasUppercaseChar)} atleast 1 uppercase</li>
          <li>{getTickIcon(hasLowercaseChar)} atleast 1 lowercase</li>
          <li>{getTickIcon(hasDigit)} atleast 1 number</li>
          <li>{getTickIcon(hasSpecialChar)} atleast 1 special character</li>
        </ul>
      </div>
    );
  }

  const validatePassword = e => {
    const { value } = e.target;
    const passwordStrength = {
      hasMin8Chars: hasMinChars(value, 8),
      hasUppercaseChar: hasUpperChar(value),
      hasLowercaseChar: hasLowerChar(value),
      hasDigit: hasNumber(value),
      hasSpecialChar: hasSpecialChar(value),
    };
    setPasswordStrength(passwordStrength);
  };

  const togglePasswordVisiblity = () => {
    setPasswordShown(passwordShown ? false : true);
  }

  const onSubmit = async (formData) => {
    const params = {
      can_create: (user && user.emailVerified && !user.mobile) ? false : true,
      mobile: formData.mobile,
      email: formData.email,
    };
    try {
      const res = await createUser(props.dispatch, params);
      const user = UserResponse(res.data);
      user.displayName = formData.name
      const userDetails = {
        ...formData,
        useMobile: true,
        user: user,
        userEmail: formData.email,
        whatsapp_consent: true,
      }
      
      if (user.isPasswordSet && user.mobileVerified) {
        props.history.push({
          pathname: '/account/verify-password',
          state: {
            ...props.location.state,
            ...userDetails,
          }
        });
      }
      else if (!user.mobileVerified || user.mobile) {
      props.history.push({
        pathname: '/account/mobile_otp',
        state: {
          ...props.location.state,
          ...userDetails,
          signup: true,
        }
      });
      }

    } catch (e) {
      console.log(e);
    }
  };

  const fetchUserData = async () => {
    try{
      const email = getValues('email');
      if(!emailRegex(email)) {
        await fetchUser(props.dispatch, { email })
      }
    }
    catch(e) {
      console.log(e);
    }
  }

  const isStaffInviteLink = AuthManager.getQueryParams().includes('invitation_code')
  return (
    <Container fluid className='vector-bg login-max-width-container pb-5'>
      <Helmet>
        <meta name="description" 
              content="Create your free PayU payments gateway account that enables
                      businesses in India to accept online payments from Credit Card, Debit Card,
                       Net Banking, BNPL, QR, UPI, Wallets and more."
        />
        <title>Create your PayU Account - PayU Payments</title>
      </Helmet>
      <Row>
          <Col md={6} className='p-0'>
            <SignInCMS />
          </Col>
        <Col md={5} className={props.isMobile && 'm-0 p-0'}>
          <div className={!props.isMobile && 'login-container mt-4 mx-4'}>
          <Card>
              <H level={5} variant="regular" className="pb-2 co-heading worksansregular-font">
                Create your PayU {isPartner && 'partner'} account
              </H>
                <div className="d-flex">
                <Caption variant="light" 
                  className={props.isMobile ? 'pt-1 align-button-text pr-2 font-weight-500'
                   : 'align-button-text pr-2 font-weight-500'}
                >
                  Already have an account?
                </Caption>
                <Form.Button
                  onClick={() =>
                    props.history.push({
                      pathname: "/account/signin",
                    })
                  }
                  variant="underlined"
                >
                  Login
                </Form.Button>
              </div>
              <form onSubmit={handleSubmit(onSubmit)} className='px-3 py-2'>
                <Row>
                  <Form.Group className="position-relative mb-3">
                    <Form.Label id="email">
                      Email
                    </Form.Label>
                    <Form.Text
                      id="email"
                      name="email"
                      type="text"
                      ref={register({
                        required: "Email can not be empty",
                        validate: { regex: emailRegex },
                      })}
                      placeholder="Enter your email id"
                      onBlur={fetchUserData}
                      autoComplete="off"
                      disabled={!!isStaffInviteLink}
                    />
                    <ErrorMessage errors={errors} name="email">
                      {({ message }) => (
                        <Form.Message
                          type="error"
                        >
                          {message}
                        </Form.Message>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Row>
                {isPartner && <Row>
                  <Form.Group className="position-relative mb-3">
                    <Form.Label id="name">
                      Company Name
                    </Form.Label>
                    <Form.Text
                      id="name"
                      name="name"
                      type="text"
                      ref={register({
                        required: "Company name can not be empty",
                        validate: { regex: nameRegex },
                      })}
                      placeholder="Enter your company name"
                      autoComplete="off"
                    />
                    <ErrorMessage errors={errors} name="name">
                      {({ message }) => (
                        <Form.Message
                          type="error"
                        >
                          {message}
                        </Form.Message>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Row>
                }
                <Row className='why-btn'>
                  <Form.Group className="position-relative mb-3">
                    <Form.Label id="password" >
                      Set a Password
                    </Form.Label>
                    <Form.Text
                      id="password"
                      name="password"
                      type={passwordShown ? "text" : "password"}
                      ref={register({
                        required: "Password can not be empty",
                        validate: { regex: strongPasswordRegex },
                      })}
                      placeholder="New Password"
                      onFocus={() => setOpen(true)}
                      onBlur={() => setOpen(false)}
                      onChange={(e) => validatePassword(e)}
                      endAdornment={{ type: `${passwordShown ? 'view-disabled' : 'view-1'}`, color: 'default', size: "18px", onClick: () => togglePasswordVisiblity() }}
                    />
                    <div className={className}>
                      <Caption variant='bold' className='mb-2 text-dark'>Your password must have:</Caption>
                      <Caption className='text-dark'>{getValidationContent()}</Caption>
                   </div>
                    <ErrorMessage errors={errors} name="password">
                      {({ message }) => (
                        <Form.Message
                          type="error"
                        >
                          {message}
                        </Form.Message>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Row>
                <Row>
                  <Form.Group className="position-relative mb-2">
                    <Form.Label id="mobile">
                      Mobile
                    </Form.Label>
                    <Form.Text
                      id="mobile"
                      name="mobile"
                      maxLength="10"
                      placeholder="Enter your 10 digit mobile number"
                      ref={register({
                        required: "Mobile can not be empty",
                        validate: { regex: mobileNumberRegex },
                      })}
                      className="pt-2 pl-5"
                      startAdornment={{ customAdornment: "+91", containerClassName: 'border-right oui-caption co-text-grey40 adornment-mobile' }}
                    />
                    <ErrorMessage errors={errors} name="mobile">
                      {({ message }) => (
                        <Form.Message
                          type="error"
                        >
                          {message}
                        </Form.Message>
                      )}
                    </ErrorMessage>
                  </Form.Group>
                </Row>
                {
                  isPartner || isStaffInviteLink ? null : <>
                    <Row>
                      <Form.Label>Do you want to collect payments on your website?</Form.Label>
                    </Row>
                    <Row className='pb-3'>
                      <Form.RadioButton
                        className='pr-2'
                        name="integrationType"
                        value={INTEGRATION_TYPE.THIRD_PARTY}
                        ref={register}
                        label='Yes'
                      />
                      <Form.RadioButton
                        name='integrationType'
                        value={INTEGRATION_TYPE.TOOLS}
                        ref={register}
                        label='No'
                      />
                    </Row>
                    {integrationType == INTEGRATION_TYPE.THIRD_PARTY && <Row>
                      <Col className='p-0'>
                        <Form.Group>
                          <Form.Text
                            className='mb-1'
                            name="website_detail.website_url"
                            placeholder='Enter your Website URL'
                            ref={register({
                              required: 'This field cannot be empty',
                              validate: { regex: websiteUrlRegex }
                            })}
                          />
                          <ErrorMessage errors={errors} name="website_detail.website_url">
                            {({ message }) => <Form.Message type="error">{message}</Form.Message>}
                          </ErrorMessage>
                        </Form.Group>
                      </Col>
                    </Row>} 
                  </>
                }
                <Row className="justify-content-end">
                  <Form.Button
                    type="submit"
                    className="w-100 mt-2 font-weight-bold rounded-btn"
                    disabled={isDisabled}
                  >
                    Send OTP & Create Account
                  </Form.Button>
                </Row>
                <Row className='pt-2'>
                  <Form.Group className="position-relative d-flex mb-3 text-center">
                    <Small id="tnc" className='pt-2 w-100'>
                      By signing up, you agree to our 
                      {" "}
                      <a href={`${process.env.PAYU_WEBSITE_HOST}/tnc`} className="highlight" target="_blank">Terms & Conditions</a>
                      {" "}
                      and
                      {" "}
                      <a href={`${process.env.PAYU_WEBSITE_HOST}/privacy-policy`} className="highlight" target="_blank">Privacy Policy</a>
                    </Small>
                  </Form.Group>
                </Row>
              </form>
          </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 768,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

const mapStateToProps = (state) => {
  return {
    user: state.session.User.data.data
  };
};
const Responsive = withSizes(mapSizesToProps)(RegisterUser);

export default connect(mapStateToProps, mapDispatchToProps)(Responsive);